/**
 * Production-safe structured logging utility
 *
 * @overview
 * - Development: Full console output with emojis and context
 * - Production: Errors to Sentry, no console pollution
 *
 * @usage
 * ```typescript
 * import { debug, info, warn, error } from '@/lib/logger';
 *
 * // Flow tracking (dev only)
 * debug('Prize restoration started', { hasAttempt, isAuthenticated });
 *
 * // Business events (could send to analytics)
 * info('Prize claimed', { userId, prizeType });
 *
 * // Warnings (Sentry breadcrumb)
 * warn('API degraded, using fallback', { service: 'google-sheets' });
 *
 * // Errors (Sentry error + stack trace)
 * error('Claim submission failed', err, { userId, orderId });
 * ```
 *
 * @security
 * Never log PII: emails, passwords, tokens, full names, phone numbers
 * Always log: user IDs, order IDs, event names, counts
 */

import { isDevelopment as isDevEnvironment } from './environment';

interface LogContext {
  [key: string]: unknown;
}

class Logger {
  private isDevelopment: boolean;
  private serviceName: string;

  constructor(serviceName: string = 'brand-website') {
    this.isDevelopment = isDevEnvironment();
    this.serviceName = serviceName;
  }

  /**
   * DEBUG: Development-only detailed logging
   * Use for: flow tracking, state inspection, variable dumps
   * Production: Silent (not needed in prod)
   */
  debug(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      console.log(`🔍 [DEBUG] ${message}`, context || '');
    }
    // Production: silent
  }

  /**
   * INFO: Business events and successful operations
   * Use for: user actions, API success, job completion
   * Production: Silent (or send to analytics if needed)
   */
  info(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      console.log(`ℹ️ [INFO] ${message}`, context || '');
    }
    // Production: Could send to analytics platform
    // Example: analytics.track(message, this.sanitize(context));
  }

  /**
   * WARN: Recoverable errors, fallback behavior, degraded service
   * Use for: API timeouts, config issues, non-critical failures
   * Production: Sentry breadcrumb (helps debug future errors)
   */
  warn(message: string, context?: LogContext): void {
    const sanitizedContext = this.sanitize(context);

    if (this.isDevelopment) {
      console.warn(`⚠️ [WARN] ${message}`, sanitizedContext || '');
    } else {
      // Production: Add breadcrumb to Sentry for error context
      // This will be available when we set up Sentry in Task 3
      const sentry = typeof window !== 'undefined' ? (window as Window & { Sentry?: { addBreadcrumb: (opts: unknown) => void } }).Sentry : undefined;
      if (sentry) {
        sentry.addBreadcrumb({
          category: 'warning',
          message,
          level: 'warning',
          data: sanitizedContext
        });
      }
    }
  }

  /**
   * ERROR: Failures, exceptions, data integrity issues
   * Use for: unhandled exceptions, API failures, critical errors
   * Production: Send to Sentry with full context
   */
  error(message: string, error?: Error | unknown, context?: LogContext): void {
    const sanitizedContext = this.sanitize(context);

    if (this.isDevelopment) {
      console.error(`❌ [ERROR] ${message}`, error, sanitizedContext || '');
    } else {
      // Production: Send to Sentry
      // This will work once we set up Sentry in Task 3
      const sentry = typeof window !== 'undefined' ? (window as Window & { Sentry?: { captureException: (err: unknown, opts: unknown) => void } }).Sentry : undefined;
      if (sentry) {
        sentry.captureException(error || new Error(message), {
          tags: {
            service: this.serviceName
          },
          extra: sanitizedContext
        });
      }
    }
  }

  /**
   * Sanitize context: Remove sensitive fields
   * Prevents accidentally logging PII
   */
  private sanitize(context?: LogContext): LogContext | undefined {
    if (!context) return undefined;

    const sanitized = { ...context };
    const sensitiveKeys = [
      'email',
      'password',
      'token',
      'apiKey',
      'secret',
      'phone',
      'address',
      'creditCard',
      'ssn',
      'name' // Use userId instead
    ];

    for (const key of Object.keys(sanitized)) {
      const lowerKey = key.toLowerCase();
      if (sensitiveKeys.some(sk => lowerKey.includes(sk))) {
        sanitized[key] = '[REDACTED]';
      }
    }

    return sanitized;
  }
}

// Singleton instance
const loggerInstance = new Logger('brand-website');

// Convenience exports
export const debug = loggerInstance.debug.bind(loggerInstance);
export const info = loggerInstance.info.bind(loggerInstance);
export const warn = loggerInstance.warn.bind(loggerInstance);
export const error = loggerInstance.error.bind(loggerInstance);

// Export logger instance for advanced usage
export const logger = loggerInstance;
